<template>
  <Modal position="center" ref="modalRef" width="37rem" @after-close="reset">
    <template #body>
      <View direction="column" gap="md">
        <View direction="column" gap="sm">
          <Text variant="h2">Request more info</Text>
          <Text variant="body-large" color="secondary">
            Tell the renter exactly what is missing and name the document where you can — for example two more recent
            pay stubs, proof of income for the last three months, or a phone number and email for their current
            landlord. Tallo sends this to the renter and re-opens their application so they can add documents, edit
            their details and submit again.
          </Text>
        </View>

        <Textarea
          class="request-message-input"
          placeholder="What do you need from the renter?"
          rows="5"
          v-model="form.fields.message"
        />

        <Hint v-if="fairHousingViolationReason" icon="alert-triangle" type="error">
          <View direction="column" gap="sm">
            <Text variant="label-medium">This may violate fair housing laws. Consider rephrasing it.</Text>
            <Text variant="body-medium">{{ fairHousingViolationReason }}</Text>
          </View>
        </Hint>
      </View>
    </template>

    <template #footer>
      <View gap="md" justify="space-between" :grow="1" class="actions">
        <Button @click="modalRef?.close()" variant="ghost">Cancel</Button>
        <Button @click="submit()" :disabled="form.validation.$invalid" :loading="form.submitting">Send request</Button>
      </View>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { required } from '@vuelidate/validators';
import { AxiosError } from 'axios';
import { ref } from 'vue';

import { Button, Hint, Modal, Text, Textarea, View, useAlertsStore, useForm } from '@tallo/design-system';

import { useRenterProfileStore } from '../store/renter-profile.store';

const props = defineProps<{
  bundleId: string;
}>();

const alerts = useAlertsStore();
const renterProfileStore = useRenterProfileStore();

const modalRef = ref<InstanceType<typeof Modal>>();
const form = useForm<{ message: string }>({ message: '' }, { message: { required } });
const fairHousingViolationReason = ref('');

function reset(): void {
  form.fields.message = '';
  form.resetValidation();
  fairHousingViolationReason.value = '';
}

async function submit(): Promise<void> {
  try {
    await form.submit(() => renterProfileStore.requestBundleUpdate(props.bundleId, form.fields.message));

    fairHousingViolationReason.value = '';
    modalRef.value?.close();
    alerts.success('Request sent to the renter');
  } catch (error) {
    // The BE Fair-Housing-screens the message and answers 406 with the reason — shown inline so the
    // owner can rephrase without losing what they typed.
    if (error instanceof AxiosError) {
      switch (error.response?.status) {
        case 406:
          fairHousingViolationReason.value = error?.response?.data?.message?.message;
          break;

        default:
          fairHousingViolationReason.value = '';
          alerts.catchError(error);
          break;
      }
    } else {
      fairHousingViolationReason.value = '';
      alerts.catchError(error);
    }
  }
}

defineExpose({ modalRef });
</script>

<style scoped lang="scss">
.actions > * {
  width: 100%;
}

.request-message-input {
  width: 100%;
}
</style>
